import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Database, ChevronLeft, ChevronRight, RefreshCw, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function DatabaseViewer() {
  const navigate = useNavigate();
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState('');
  const [documents, setDocuments] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [limit] = useState(20);
  const [isLoading, setIsLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    loadCollections();
  }, []);

  useEffect(() => {
    if (selectedCollection) {
      loadDocuments();
    }
  }, [selectedCollection, skip]);

  const loadCollections = async () => {
    try {
      const response = await axios.get(`${API}/admin/db-viewer/collections`, { withCredentials: true });
      setCollections(response.data);
      setHasAccess(true);
      if (response.data.length > 0) {
        setSelectedCollection(response.data[0]);
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied - Super admin only');
        setHasAccess(false);
      } else {
        toast.error('Failed to load collections');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadDocuments = async () => {
    if (!selectedCollection) return;
    try {
      const response = await axios.get(
        `${API}/admin/db-viewer/collection/${selectedCollection}?limit=${limit}`,
        { withCredentials: true }
      );
      setDocuments(response.data.documents);
      setTotal(response.data.count);
    } catch (error) {
      toast.error('Failed to load documents');
    }
  };

  const handleDeleteDocument = async (doc) => {
    // Find a unique identifier for the document
    const identifier = doc.user_id || doc.list_id || doc.match_id || doc.club_id || doc.invite_id || doc.notification_id;
    const key = doc.user_id ? 'user_id' : doc.list_id ? 'list_id' : doc.match_id ? 'match_id' : doc.club_id ? 'club_id' : doc.invite_id ? 'invite_id' : 'notification_id';
    
    if (!identifier) {
      toast.error('Cannot identify document for deletion');
      return;
    }
    
    if (!confirm(`Delete document with ${key}: ${identifier}?`)) return;
    
    try {
      await axios.delete(`${API}/super-admin/collection/${selectedCollection}/document`, {
        data: { [key]: identifier },
        withCredentials: true
      });
      toast.success('Document deleted');
      loadDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#007AFF]"></div>
          <p className="mt-4 text-white">Loading...</p>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-white">
        <Card className="bg-[#111111] border-[#27272A] max-w-md">
          <CardContent className="pt-6 text-center">
            <Database className="w-16 h-16 text-[#FF3B30] mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
            <p className="text-gray-400 mb-4">This page is restricted to the super administrator only.</p>
            <Button onClick={() => navigate('/dashboard')} className="bg-[#007AFF] hover:bg-[#0066D6] text-white rounded-full">
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Button 
          onClick={() => navigate('/admin')}
          variant="ghost"
          className="text-white hover:bg-white/10 mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Admin
        </Button>

        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <Database className="w-10 h-10 text-[#007AFF]" />
            <h1 className="text-3xl font-semibold" style={{ fontFamily: "'Inter', sans-serif" }}>
              Database Viewer
            </h1>
          </div>
          <p className="text-gray-400">Super Admin MongoDB Management</p>
        </div>

        <Card className="bg-[#111111] border-[#27272A] mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white">Collection Browser</CardTitle>
              <div className="flex items-center gap-4">
                <Select value={selectedCollection} onValueChange={(val) => { setSelectedCollection(val); setSkip(0); }}>
                  <SelectTrigger className="w-48 bg-[#0A0A0A] border-[#27272A] text-white">
                    <SelectValue placeholder="Select collection" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111111] border-[#27272A]">
                    {collections.map(col => (
                      <SelectItem key={col} value={col} className="text-white hover:bg-[#27272A]">
                        {col}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={loadDocuments} variant="outline" className="border-[#27272A] text-white">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-gray-400 mb-4">
              Showing {skip + 1} - {Math.min(skip + limit, total)} of {total} documents
            </div>
            
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {documents.map((doc, idx) => (
                <div key={idx} className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <pre className="text-xs text-gray-300 overflow-x-auto flex-1">
                      {JSON.stringify(doc, null, 2)}
                    </pre>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleDeleteDocument(doc)}
                      className="text-[#FF3B30] hover:bg-[#FF3B30]/10 ml-2"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#27272A]">
              <Button 
                onClick={() => setSkip(Math.max(0, skip - limit))}
                disabled={skip === 0}
                variant="outline"
                className="border-[#27272A] text-white"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Previous
              </Button>
              <span className="text-gray-400">
                Page {Math.floor(skip / limit) + 1} of {Math.ceil(total / limit)}
              </span>
              <Button 
                onClick={() => setSkip(skip + limit)}
                disabled={skip + limit >= total}
                variant="outline"
                className="border-[#27272A] text-white"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default DatabaseViewer;
